import React, { Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import '../static/css/ResetPassword.css';
import logo from '../static/images/images/a-logo-transperent.png';

const eyeIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const eyeOffIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const checkIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const dotIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="3" />
    </svg>
);

class ResetPasswordInner extends Component {
    constructor(props) {
        super(props);
        this.state = {
            status: 'verifying', // verifying | ready | expired | invalid | error | submitting | success
            email: '',
            newPassword: '',
            confirmNewPassword: '',
            showNew: false,
            showConfirm: false,
            submitError: '',
        };
        this.onChangeNewPassword = this.onChangeNewPassword.bind(this);
        this.onChangeConfirmNewPassword = this.onChangeConfirmNewPassword.bind(this);
        this.toggleShowNew = this.toggleShowNew.bind(this);
        this.toggleShowConfirm = this.toggleShowConfirm.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        this.verifyLink();
    }

    async verifyLink() {
        const { id, token } = this.props.params;
        try {
            const response = await axios.get(
                process.env.REACT_APP_SERVER_API + `/auth/verify-reset-password-link/${id}/${token}`
            );
            this.setState({ status: 'ready', email: response.data || '' });
        } catch (err) {
            const status = err.response && err.response.status;
            const data = err.response && err.response.data;
            const errMsg = (data && data.error) || '';
            if (status === 401 && errMsg.toLowerCase().includes('expired')) {
                this.setState({ status: 'expired' });
            } else if (status === 401) {
                this.setState({ status: 'invalid' });
            } else {
                this.setState({ status: 'error' });
            }
        }
    }

    onChangeNewPassword(e) {
        this.setState({ newPassword: e.target.value, submitError: '' });
    }

    onChangeConfirmNewPassword(e) {
        this.setState({ confirmNewPassword: e.target.value, submitError: '' });
    }

    toggleShowNew() {
        this.setState((prev) => ({ showNew: !prev.showNew }));
    }

    toggleShowConfirm() {
        this.setState((prev) => ({ showConfirm: !prev.showConfirm }));
    }

    getRuleChecks() {
        const { newPassword, confirmNewPassword } = this.state;
        return {
            length: newPassword.length >= 8 && newPassword.length <= 64,
            number: /\d/.test(newPassword),
            special: /^(?=.*[^a-zA-Z0-9])[\x21-\x7E]+$/.test(newPassword),
            matches: newPassword.length > 0 && newPassword === confirmNewPassword,
        };
    }

    isFormValid() {
        const checks = this.getRuleChecks();
        return checks.length && checks.number && checks.special && checks.matches;
    }

    async onSubmit(e) {
        e.preventDefault();
        if (!this.isFormValid() || this.state.status === 'submitting') return;
        const { id, token } = this.props.params;
        this.setState({ status: 'submitting', submitError: '' });
        try {
            await axios.post(
                process.env.REACT_APP_SERVER_API + `/auth/reset-password/${id}/${token}`,
                {
                    new_password: this.state.newPassword,
                    confirm_new_password: this.state.confirmNewPassword,
                }
            );
            this.setState({ status: 'success' });
        } catch (err) {
            const status = err.response && err.response.status;
            const data = err.response && err.response.data;
            let message = 'Something went wrong. Please try again.';
            if (status === 401 && data && data.error) {
                if (data.error.toLowerCase().includes('expired')) {
                    this.setState({ status: 'expired' });
                    return;
                }
                message = data.error;
            } else if (status === 401 && typeof data === 'object') {
                // Validation errors object — pick the first message
                const firstKey = Object.keys(data)[0];
                if (firstKey) message = data[firstKey];
            }
            this.setState({ status: 'ready', submitError: message });
        }
    }

    renderRulesChecklist() {
        const checks = this.getRuleChecks();
        const items = [
            { key: 'length', label: '8–64 characters', ok: checks.length },
            { key: 'number', label: 'Contains a number', ok: checks.number },
            { key: 'special', label: 'Contains a special character (any symbol except spaces)', ok: checks.special },
            { key: 'matches', label: 'Matches confirm password', ok: checks.matches },
        ];
        return (
            <ul className="reset-password-rules">
                {items.map((item) => (
                    <li key={item.key} className={`reset-password-rule ${item.ok ? 'ok' : ''}`}>
                        <span className="reset-password-rule-icon">{item.ok ? checkIcon : dotIcon}</span>
                        <span className="reset-password-rule-label">{item.label}</span>
                    </li>
                ))}
            </ul>
        );
    }

    renderForm() {
        const { showNew, showConfirm, newPassword, confirmNewPassword, status, submitError, email } = this.state;
        const valid = this.isFormValid();
        const submitting = status === 'submitting';
        const buttonDisabled = !valid || submitting;
        return (
            <form className="reset-password-form" onSubmit={this.onSubmit}>
                {email && (
                    <p className="reset-password-current-email">
                        Resetting password for: <span className="reset-password-current-email-value">{email}</span>
                    </p>
                )}

                <label className="reset-password-form-label" htmlFor="reset-new-password">New password</label>
                <div className="reset-password-form-input-wrapper">
                    <input
                        id="reset-new-password"
                        type={showNew ? 'text' : 'password'}
                        className="reset-password-form-input"
                        value={newPassword}
                        onChange={this.onChangeNewPassword}
                        autoComplete="new-password"
                    />
                    <button type="button" className="reset-password-form-toggle-password" onClick={this.toggleShowNew} aria-label={showNew ? 'Hide password' : 'Show password'}>
                        {showNew ? eyeIcon : eyeOffIcon}
                    </button>
                </div>

                <label className="reset-password-form-label" htmlFor="reset-confirm-password">Confirm new password</label>
                <div className="reset-password-form-input-wrapper">
                    <input
                        id="reset-confirm-password"
                        type={showConfirm ? 'text' : 'password'}
                        className="reset-password-form-input"
                        value={confirmNewPassword}
                        onChange={this.onChangeConfirmNewPassword}
                        autoComplete="new-password"
                    />
                    <button type="button" className="reset-password-form-toggle-password" onClick={this.toggleShowConfirm} aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                        {showConfirm ? eyeIcon : eyeOffIcon}
                    </button>
                </div>
                {this.renderRulesChecklist()}

                {submitError && <p className="reset-password-error">{submitError}</p>}
                <button type="submit" className="reset-password-form-submit" disabled={buttonDisabled}>
                    {submitting ? 'Updating…' : 'Reset Password'}
                </button>
            </form>
        );
    }

    renderContent() {
        const { status } = this.state;

        if (status === 'verifying') {
            return (
                <div className="reset-password-state-card">
                    <div className="loading-spinner"></div>
                    <p className="reset-password-state-message">Checking your reset link…</p>
                </div>
            );
        }

        if (status === 'ready' || status === 'submitting') {
            return this.renderForm();
        }

        if (status === 'expired') {
            return (
                <div className="reset-password-state-card">
                    <div className="reset-password-state-icon reset-password-state-icon-error">✕</div>
                    <h3 className="reset-password-state-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Link expired</h3>
                    <p className="reset-password-state-message">
                        This reset link has expired. Reset links are valid for 15 minutes — please request a new one.
                    </p>
                    <Link to="/forgot-password" className="reset-password-state-button">Request New Link</Link>
                </div>
            );
        }

        if (status === 'invalid') {
            return (
                <div className="reset-password-state-card">
                    <div className="reset-password-state-icon reset-password-state-icon-error">✕</div>
                    <h3 className="reset-password-state-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Invalid link</h3>
                    <p className="reset-password-state-message">
                        This reset link is no longer valid. It may have already been used or generated from a different password.
                    </p>
                    <Link to="/forgot-password" className="reset-password-state-button">Request New Link</Link>
                </div>
            );
        }

        if (status === 'success') {
            return (
                <div className="reset-password-state-card">
                    <div className="reset-password-state-icon reset-password-state-icon-success">✓</div>
                    <h3 className="reset-password-state-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Password reset!</h3>
                    <p className="reset-password-state-message">
                        Your password has been updated. Sign in with your new password to continue.
                    </p>
                    <Link to="/signin" className="reset-password-state-button">Sign In</Link>
                </div>
            );
        }

        // generic error
        return (
            <div className="reset-password-state-card">
                <div className="reset-password-state-icon reset-password-state-icon-error">✕</div>
                <h3 className="reset-password-state-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Something went wrong</h3>
                <p className="reset-password-state-message">
                    We couldn't process this reset link. Please try requesting a new one or contact us at hello@animates.app if the problem continues.
                </p>
                <Link to="/forgot-password" className="reset-password-state-button">Request New Link</Link>
            </div>
        );
    }

    render() {
        return (
            <div className="reset-password-container">
                <div className="animates-logo-container">
                    <Link to="/"><img className="animates-logo hover" style={{width: "125px"}} src={logo} alt="" /></Link>
                </div>
                <h2 className="color-2 reset-password-title" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Reset Password</u></h2>
                {this.renderContent()}
                <p className="reset-password-footer">© 2026 AniMates. All rights reserved.</p>
            </div>
        );
    }
}

// Wrapper to pass React Router v6 params to the class component (matches VerifyEmail.js pattern)
function ResetPassword() {
    const params = useParams();
    return <ResetPasswordInner params={params} />;
}

export default ResetPassword;
